import { useSwipe } from './useSwipe';

const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
const EASE = 'transform 180ms ease-out, opacity 180ms ease-out';

/**
 * Moves the page without React: re-rendering a whole week or month on every finger move made
 * tablets flicker (Mike, 19.10.: "Beim Swipen flackert alles, über die Pfeile nicht").
 */
function paint(el: HTMLElement, x: number, opacity: number, ease: boolean) {
  const s = el.style;
  s.transition = ease ? EASE : 'none';
  s.transform = x ? `translateX(${x}px)` : '';
  s.opacity = opacity < 1 ? String(opacity) : '';
}

/**
 * A page that follows the finger and turns like a carousel (Mike, 19.10.: "dass es dem Finger
 * folgt und die nächste Ansicht wie ein Karussell reinfährt, ggf. mit einem Fading"). The old
 * view slides out and fades, the new one slides in from the other side. "Bewegung reduzieren"
 * turns the page without movement.
 */
export function useSwipePane<T extends HTMLElement>(
  onStep: (dir: 1 | -1) => void,
  options: { scrollX?: boolean; onPull?: (dy: number, done: boolean) => void } = {},
) {
  const ref = useSwipe<T>({
    layer: true,
    scrollX: options.scrollX,
    onPull: options.onPull,
    // follows the finger, a little damped; back to rest with a short ease
    onDrag: (dx, el) => paint(el, dx * 0.9, 1 - Math.min(Math.abs(dx) / 600, 0.4), dx === 0),
    onSwipe: (dir, el) => {
      if (reduced()) return onStep(dir);
      const w = el.clientWidth;
      paint(el, -dir * w * 0.5, 0, true);
      window.setTimeout(() => {
        onStep(dir);
        paint(el, dir * w * 0.35, 0, false);
        requestAnimationFrame(() => requestAnimationFrame(() => paint(el, 0, 1, true)));
      }, 170);
    },
  });
  // the movement is painted straight onto the element; kept for the callers' markup
  return { ref, style: undefined };
}
